import { isNumber, object } from '@art-suite/art-core-ts';
import { useMemo, useRef } from 'react';

import {
  ApiScan,
  getScanInlineProductFromOrgProduct,
} from '@kaiizen/kai-pro-api';
import {
  addScanGroup,
  addStandardScanLineItem,
  addStandardScanOption,
  cloneGroup,
  deleteScanGroup,
  deleteScanLineItem,
  deleteScanOption,
  deleteScanTask,
  getEffectiveScanValue,
  getScanLineItem,
  getScanOption,
  getScanPartType,
  getScanWithRevertedTask,
  getScanWithUpdatedGroup,
  getScanWithUpdatedLineItem,
  getScanWithUpdatedOption,
  getScanWithUpdatedTask,
  getScopeRevertedScan,
  getUpdatedScan,
  PartialNullable,
  ScanGroupOptimized,
  ScanLineItem,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
  ScanValue,
} from '@kaiizen/kai-scan-template-system';

import { useOrgProductsByIdForScan } from '../apiState';

const isNonZeroValue = (value: ScanValue) => {
  const a = getEffectiveScanValue(value);
  return isNumber(a) && Math.abs(a) > 0.001;
};

const lineItemHasUnsetPrice = (lineItem: ScanLineItemOptimized) => {
  return (
    lineItem.template.values?.price && !isNonZeroValue(lineItem.values.price)
  );
};

export type ScanDataMutators = {
  scanId: string | undefined;
  setScan: (
    scan:
      | ScanRootOptimized
      | ((currentScan: ScanRootOptimized) => ScanRootOptimized),
  ) => Promise<ScanRootOptimized>;

  /**
   * Updates the scan root. You do not need to pass in unchanged fields. The updates are merged into the current scan root.
   * Note, certain sub-objects are also merged: values & customerMetadata. To erase fields when merging, pass in null.
   * .photos, however, as an array, should be updated using this pattern: {photos: getUniqueScanPhotos(existingPhotos, newPhotos)}
   *
   * This same logic applies to all update methods.
   * @param updates - The updates to apply to the scan root.
   * @returns The updated scan root.
   */
  updateScan: (
    updates: PartialNullable<ScanRootOptimized>,
  ) => Promise<ScanRootOptimized>;

  addGroup: (
    groupTemplateId: string,
    customLabel?: string,
  ) => Promise<ScanRootOptimized>;
  cloneGroup: (groupId: string) => Promise<ScanRootOptimized>;
  updateGroup: (
    groupId: string,
    updates: Partial<ScanGroupOptimized>,
  ) => Promise<ScanRootOptimized>;
  deleteGroup: (groupId: string) => Promise<ScanRootOptimized>;

  updateTask: (
    groupId: string,
    taskId: string,
    updates: Partial<ScanTaskOptimized>,
  ) => Promise<ScanRootOptimized>;
  deleteTask: (groupId: string, taskId: string) => Promise<ScanRootOptimized>;

  addOption: ({
    groupId,
    taskId,
    optionTemplateId,
    addLineItemTemplateId,
    addLineItemInitializers,
  }: {
    groupId: string;
    taskId: string;
    optionTemplateId: string;
    addLineItemTemplateId?: string;
    addLineItemInitializers?: Partial<ScanLineItem>;
  }) => Promise<ScanRootOptimized>;
  addLineItem: ({
    groupId,
    taskId,
    optionId,
    lineItemTemplateId,
    initializers,
  }: {
    groupId: string;
    taskId: string;
    optionId: string;
    lineItemTemplateId: string;
    initializers?: Partial<ScanLineItem>;
  }) => Promise<ScanRootOptimized>;

  updateOption: (
    groupId: string,
    taskId: string,
    optionId: string,
    updates: Partial<ScanOptionOptimized>,
  ) => Promise<ScanRootOptimized>;
  deleteOption: (
    groupId: string,
    taskId: string,
    optionId: string,
  ) => Promise<ScanRootOptimized>;

  updateLineItem: (
    groupId: string,
    taskId: string,
    optionId: string,
    lineItemId: string,
    updates: Partial<ScanLineItem>,
  ) => Promise<ScanRootOptimized>;
  /** Removes the line item from its option; the option itself remains, even when emptied. */
  deleteLineItem: (
    groupId: string,
    taskId: string,
    optionId: string,
    lineItemId: string,
  ) => Promise<ScanRootOptimized>;
  updateScanPart: (
    {
      groupId,
      taskId,
      optionId,
      lineItemId,
    }: {
      groupId?: string;
      taskId?: string;
      optionId?: string;
      lineItemId?: string;
    },
    updates: Partial<ScanPartOptimized>,
  ) => Promise<ScanRootOptimized>;

  /**
   * Reverts the scope changes of a scan back to baseline.
   * NOTE: does not alter the scan record - just reverts all changes to the scan root (scan data). i.e. if the scan was type 'changeRequest', it will remain a change request.
   * - Reverts assignedContractorId back to baseline
   * - Reverts all values back to baseline
   * - Reverts options structure (restores deleted, removes added)
   * - Recursively reverts changed options
   * @param scan - The scan to revert
   * @param baselineScan - The baseline scan to revert to
   * @returns A new scan with the scope reverted
   */
  revertScopeChanges: (
    can: ScanRootOptimized,
    baselineScan: ScanRootOptimized,
  ) => Promise<ScanRootOptimized>;

  /**
   * Reverts a task's scope changes back to baseline: removes it when the baseline doesn't have it, restores it when it
   * was deleted, and otherwise reverts it in place (its contractor, values, options and line items).
   * @param baselineScan - The baseline scan to revert to
   * @param groupId - The task's group
   * @param taskId - The task to revert, in the scan, the baseline, or both
   * @returns A new scan with the task reverted
   */
  revertTaskScopeChanges: (
    baselineScan: ScanRootOptimized,
    groupId: string,
    taskId: string,
  ) => Promise<ScanRootOptimized>;
};
export const useScanDataMutatorsWithUpdateNotification = (
  scanMutators: ScanDataMutators,
  updateNotification?: () => void,
  failureNotification?: () => void,
) =>
  useMemo(() => {
    const tapNotification = (updatedScan: ScanRootOptimized) => {
      try {
        updateNotification?.();
      } catch (error) {
        console.error('Error in updateNotification', error);
      }
      return updatedScan;
    };
    const tapFailureNotification = (error: Error) => {
      try {
        console.error(
          'ScanDataMutatorsWithUpdateNotification: failureNotification',
          error,
        );
        failureNotification?.();
      } catch (error2) {
        console.error('Error in failureNotification', error2);
      }
      throw error;
    };
    return {
      ...scanMutators,
      setScan: async (
        scan:
          | ScanRootOptimized
          | ((currentScan: ScanRootOptimized) => ScanRootOptimized),
      ) =>
        scanMutators
          .setScan(scan)
          .then(tapNotification, tapFailureNotification),
      updateScan: async (updates: PartialNullable<ScanRootOptimized>) =>
        scanMutators
          .updateScan(updates)
          .then(tapNotification, tapFailureNotification),
      updateGroup: async (
        groupId: string,
        updates: Partial<ScanGroupOptimized>,
      ) =>
        scanMutators
          .updateGroup(groupId, updates)
          .then(tapNotification, tapFailureNotification),
      addGroup: async (groupTemplateId: string, customLabel?: string) =>
        scanMutators
          .addGroup(groupTemplateId, customLabel)
          .then(tapNotification, tapFailureNotification),
      deleteGroup: async (groupId: string) =>
        scanMutators
          .deleteGroup(groupId)
          .then(tapNotification, tapFailureNotification),
      updateTask: async (
        groupId: string,
        taskId: string,
        updates: Partial<ScanTaskOptimized>,
      ) =>
        scanMutators
          .updateTask(groupId, taskId, updates)
          .then(tapNotification, tapFailureNotification),
      updateOption: async (
        groupId: string,
        taskId: string,
        optionId: string,
        updates: Partial<ScanOptionOptimized>,
      ) =>
        scanMutators
          .updateOption(groupId, taskId, optionId, updates)
          .then(tapNotification, tapFailureNotification),
      addOption: async (params: {
        groupId: string;
        taskId: string;
        optionTemplateId: string;
        addLineItemTemplateId?: string;
        addLineItemInitializers?: Partial<ScanLineItem>;
      }) =>
        scanMutators
          .addOption(params)
          .then(tapNotification, tapFailureNotification),
      deleteOption: async (groupId: string, taskId: string, optionId: string) =>
        scanMutators
          .deleteOption(groupId, taskId, optionId)
          .then(tapNotification, tapFailureNotification),
      addLineItem: async (params: {
        groupId: string;
        taskId: string;
        optionId: string;
        lineItemTemplateId: string;
        initializers?: Partial<ScanLineItem>;
      }) =>
        scanMutators
          .addLineItem(params)
          .then(tapNotification, tapFailureNotification),
      updateLineItem: async (
        groupId: string,
        taskId: string,
        optionId: string,
        lineItemId: string,
        updates: Partial<ScanLineItem>,
      ) =>
        scanMutators
          .updateLineItem(groupId, taskId, optionId, lineItemId, updates)
          .then(tapNotification, tapFailureNotification),
      deleteLineItem: async (
        groupId: string,
        taskId: string,
        optionId: string,
        lineItemId: string,
      ) =>
        scanMutators
          .deleteLineItem(groupId, taskId, optionId, lineItemId)
          .then(tapNotification, tapFailureNotification),
      revertScopeChanges: async (
        scan: ScanRootOptimized,
        baselineScan: ScanRootOptimized,
      ) =>
        scanMutators
          .revertScopeChanges(scan, baselineScan)
          .then(tapNotification, tapFailureNotification),
      revertTaskScopeChanges: async (
        baselineScan: ScanRootOptimized,
        groupId: string,
        taskId: string,
      ) =>
        scanMutators
          .revertTaskScopeChanges(baselineScan, groupId, taskId)
          .then(tapNotification, tapFailureNotification),
    };
  }, [scanMutators, updateNotification, failureNotification]);

export const useScanDataMutators = (
  scan: ApiScan | undefined,
  setScanData: (
    scanUpdater: (
      currentScan: ScanRootOptimized,
    ) => ScanRootOptimized | Promise<ScanRootOptimized>,
  ) => Promise<ScanRootOptimized>,
): ScanDataMutators => {
  const { orgProductsById } = useOrgProductsByIdForScan(scan);
  const orgProductsByIdRef = useRef(orgProductsById);
  orgProductsByIdRef.current = orgProductsById;
  const scanMutators = useMemo((): ScanDataMutators => {
    return {
      scanId: scan?.id,
      setScan: (
        scanOrScanTransformer:
          | ScanRootOptimized
          | ((currentScan: ScanRootOptimized) => ScanRootOptimized),
      ) =>
        setScanData(currentScan =>
          typeof scanOrScanTransformer === 'function'
            ? scanOrScanTransformer(currentScan)
            : scanOrScanTransformer
        ),

      updateScan: updates => setScanData(currentScan => getUpdatedScan(currentScan, updates)),
      updateGroup: (groupId, updates) =>
        setScanData(currentScanRoot =>
          /* this one */ getScanWithUpdatedGroup(
            currentScanRoot,
            groupId,
            updates,
          )
        ),
      addGroup: (groupTemplateId, customLabel) => setScanData(currentScan => addScanGroup(currentScan, groupTemplateId, customLabel)),
      deleteGroup: groupId => setScanData(currentScan => deleteScanGroup(currentScan, groupId)),
      updateTask: (groupId, taskId, updates) => setScanData(currentScan => getScanWithUpdatedTask(currentScan, groupId, taskId, updates)),
      deleteTask: (groupId, taskId) => setScanData(currentScan => deleteScanTask(currentScan, groupId, taskId)),
      updateOption: (groupId, taskId, optionId, updates) =>
        setScanData(currentScan =>
          getScanWithUpdatedOption(
            currentScan,
            groupId,
            taskId,
            optionId,
            updates,
          )
        ),
      addOption: ({
        groupId,
        taskId,
        optionTemplateId,
        addLineItemTemplateId,
        addLineItemInitializers,
      }) =>
        setScanData(async currentScan => {
          const inlineProductsById = object(
            orgProductsByIdRef.current,
            getScanInlineProductFromOrgProduct,
          );
          return addStandardScanOption({
            scan: currentScan,
            groupId,
            taskId,
            optionTemplateId,
            inlineProductsById,
            addLineItemTemplateId,
            addLineItemInitializers,
          });
        }),
      cloneGroup: groupId => setScanData(currentScan => cloneGroup(currentScan, groupId)),
      deleteOption: (groupId, taskId, optionId) => setScanData(currentScan => deleteScanOption(currentScan, groupId, taskId, optionId)),
      addLineItem: ({
        groupId,
        taskId,
        optionId,
        lineItemTemplateId,
        initializers,
      }) =>
        setScanData(currentScan => {
          const inlineProductsById = object(
            orgProductsByIdRef.current,
            getScanInlineProductFromOrgProduct,
          );
          const scanWithAddedLineItem = addStandardScanLineItem(
            currentScan,
            groupId,
            taskId,
            optionId,
            lineItemTemplateId,
            inlineProductsById,
          );
          if (initializers) {
            const option = getScanOption(
              scanWithAddedLineItem,
              groupId,
              taskId,
              optionId,
            );
            const newLineItem = option.lineItems?.find(
              li => li.templateId === lineItemTemplateId,
            );
            if (!newLineItem) {
              throw new Error(
                `Line item with template id ${lineItemTemplateId} not found`,
              );
            }
            return getScanWithUpdatedLineItem(
              scanWithAddedLineItem,
              groupId,
              taskId,
              optionId,
              newLineItem.id,
              initializers,
            );
          }
          return scanWithAddedLineItem;
        }),
      updateLineItem: (groupId, taskId, optionId, lineItemId, updates) =>
        setScanData(async currentScan => {
          const scanRootPass1 = getScanWithUpdatedLineItem(
            currentScan,
            groupId,
            taskId,
            optionId,
            lineItemId,
            updates,
          );
          const lineItem = getScanLineItem(
            scanRootPass1,
            groupId,
            taskId,
            optionId,
            lineItemId,
          );
          if (
            lineItem.skuId
            && (!lineItem.skuDetails || lineItemHasUnsetPrice(lineItem))
          ) {
            const orgProduct = orgProductsByIdRef.current?.[lineItem.skuId];
            if (orgProduct) {
              return getScanWithUpdatedLineItem(
                scanRootPass1,
                groupId,
                taskId,
                optionId,
                lineItemId,
                {
                  skuDetails: getScanInlineProductFromOrgProduct(orgProduct),
                  values: {
                    price: { setValue: orgProduct.price },
                  },
                },
              );
            }
          }
          return scanRootPass1;
        }),
      deleteLineItem: (groupId, taskId, optionId, lineItemId) =>
        setScanData(currentScan => deleteScanLineItem(currentScan, groupId, taskId, optionId, lineItemId)),
      updateScanPart: (
        { groupId, taskId, optionId, lineItemId },
        updates: Partial<ScanPartOptimized>,
      ) => {
        const scanPartType = getScanPartType(
          groupId,
          taskId,
          optionId,
          lineItemId,
        );
        switch (scanPartType) {
          case 'scan':
            return setScanData(currentScan => getUpdatedScan(currentScan, updates));
          case 'group':
            return setScanData(currentScan => getScanWithUpdatedGroup(currentScan, groupId!, updates));
          case 'task':
            return setScanData(currentScan => getScanWithUpdatedTask(currentScan, groupId!, taskId!, updates));
          case 'option':
            return setScanData(currentScan =>
              getScanWithUpdatedOption(
                currentScan,
                groupId!,
                taskId!,
                optionId!,
                updates,
              )
            );
          case 'lineItem':
            return setScanData(currentScan =>
              getScanWithUpdatedLineItem(
                currentScan,
                groupId!,
                taskId!,
                optionId!,
                lineItemId!,
                updates,
              )
            );
          default:
            throw new Error(`Invalid scan part type: ${scanPartType}`);
        }
      },
      revertScopeChanges: (
        scan: ScanRootOptimized,
        baselineScan: ScanRootOptimized,
      ) => setScanData(currentScan => getScopeRevertedScan(currentScan, baselineScan)),
      revertTaskScopeChanges: (
        baselineScan: ScanRootOptimized,
        groupId: string,
        taskId: string,
      ) => setScanData(currentScan => getScanWithRevertedTask(currentScan, baselineScan, groupId, taskId)),
    };
  }, [scan?.id, setScanData]);
  return scanMutators;
};
